import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import React, { useMemo } from "react";
import { useLocation } from "react-router";

import type { NavItemType } from "./NavItem";
import { NavItem } from "./NavItem";
import { UserHeader } from "./UserHeader";
import { drawerWidth } from "./constants";

import type { User } from "@/models";

export type SidebarProps = {
    user: User | null;
    theme: "system" | "light" | "dark";
    setTheme: (mode: "system" | "light" | "dark") => void;
    logout: () => Promise<void> | void;
    items: NavItemType[];
    mobileOpen?: boolean;
    onCloseMobile?: () => void;
};

export function Sidebar({
    user,
    theme,
    setTheme,
    logout,
    items,
    mobileOpen,
    onCloseMobile,
}: SidebarProps) {
    const location = useLocation();

    const groups = useMemo(() => {
        const result: NavItemType[][] = [];
        let currentGroup: NavItemType[] = [];
        for (const item of items.filter(
            (i) => i.type === "divider" || i.visible !== false,
        )) {
            if (item.type === "divider") {
                if (currentGroup.length > 0) {
                    result.push(currentGroup);
                    currentGroup = [];
                }
            } else {
                currentGroup.push(item);
            }
        }
        if (currentGroup.length > 0) {
            result.push(currentGroup);
        }
        return result;
    }, [items]);

    const content = (
        <Box component="nav">
            <UserHeader
                user={user}
                theme={theme}
                setTheme={setTheme}
                logout={logout}
            />
            <Divider />
            {groups.map((group, groupIdx) => (
                <React.Fragment key={`group-${groupIdx}`}>
                    <List aria-label={`navigation group ${groupIdx + 1}`}>
                        {group.map((item, idx) => (
                            <NavItem
                                key={`nav-${groupIdx}-${idx}`}
                                {...item}
                                // Only compute active for link items
                                active={
                                    item.type === "link" &&
                                    (location.pathname === item.to ||
                                        (item.to !== "/" &&
                                            location.pathname.startsWith(
                                                item.to + "/",
                                            )))
                                }
                            />
                        ))}
                    </List>
                    {groupIdx < groups.length - 1 && <Divider />}
                </React.Fragment>
            ))}
        </Box>
    );

    return (
        <>
            {/* Mobile temporary drawer */}
            <Drawer
                variant="temporary"
                open={!!mobileOpen}
                onClose={onCloseMobile}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: "block", md: "none" },
                    ["& .MuiDrawer-paper"]: {
                        width: drawerWidth,
                        boxSizing: "border-box",
                        borderRight: "none",
                        borderRadius: 0,
                    },
                }}
            >
                {content}
            </Drawer>
            {/* Desktop permanent drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: "none", md: "block" },
                    width: drawerWidth,
                    ["& .MuiDrawer-paper"]: {
                        width: drawerWidth,
                        boxSizing: "border-box",
                        borderRight: "none",
                        borderRadius: 0,
                    },
                }}
                open
            >
                {content}
            </Drawer>
        </>
    );
}
